import { ReactNode } from 'react';

export type OnboardingStep =
  | 'type'
  | 'setup'
  | 'personal-setup'
  | 'outcomes'
  | 'pricing'
  | 'review'
  | 'invites'
  | 'complete';

interface OnboardingLayoutProps {
  currentStep: OnboardingStep;
  children: ReactNode;
  workspaceType?: 'personal' | 'team' | 'enterprise' | null;
  onboardingMode?: 'business' | 'personal' | null;
  wide?: boolean;
}

const businessSteps: { key: OnboardingStep; label: string }[] = [
  { key: 'setup', label: 'Setup' },
  { key: 'outcomes', label: 'Outcomes' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'review', label: 'Review' },
  { key: 'invites', label: 'Invites' },
  { key: 'complete', label: 'Complete' },
];

const personalSteps: { key: OnboardingStep; label: string }[] = [
  { key: 'personal-setup', label: 'Setup' },
  { key: 'outcomes', label: 'Outcomes' },
  { key: 'pricing', label: 'Plan' },
  { key: 'complete', label: 'Complete' },
];

/**
 * Shared layout for onboarding flow with progress indicator.
 *
 * Personal mode shows the 4-step bar (Setup → Outcomes → Plan → Complete).
 * Business mode shows the 6-step bar (Setup → Outcomes → Pricing → Review →
 * Invites → Complete), with the invites step filtered out when
 * workspace.type === 'personal' — the long-standing rule for business-mode
 * users with a personally-typed workspace.
 *
 * When `onboardingMode` is `null` / `undefined` (caller hasn't loaded
 * onboarding state yet), the progress bar renders a neutral path-agnostic
 * **skeleton** rather than defaulting to the business 8-step layout.
 * That default previously caused a visible "8 → 3" flicker for personal
 * users whose pages mounted with `onboardingMode = null` before the
 * `/api/onboarding/state` fetch resolved.
 *
 * Business-only screens (setup, outcomes, review, invites) pass
 * `onboardingMode="business"` explicitly so they render the business bar
 * immediately, without going through the skeleton.
 */
export function OnboardingLayout({ currentStep, children, workspaceType, onboardingMode, wide = false }: OnboardingLayoutProps) {
  // Path-agnostic skeleton when the caller hasn't told us the mode yet.
  const modeKnown = onboardingMode === 'personal' || onboardingMode === 'business';

  if (!modeKnown) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Skeleton progress bar — same outer shape as the real bar so the
            layout doesn't jump when the mode resolves, but no labels and
            no step count are revealed. */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div
              role="status"
              aria-label="Loading onboarding progress"
              className="h-8 w-full bg-gray-200 rounded animate-pulse"
            />
          </div>
        </div>
        <div className="flex-1 flex items-start justify-center p-4 py-8">
          <div className={`w-full ${wide ? 'max-w-5xl' : 'max-w-2xl'}`}>
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Personal onboarding mode: simplified step sequence
  const steps = onboardingMode === 'personal' ? personalSteps : businessSteps;

  // Business mode: personal workspaces skip invites step
  const visibleSteps = onboardingMode !== 'personal' && workspaceType === 'personal'
    ? steps.filter(s => s.key !== 'invites')
    : steps;

  const currentIndex = visibleSteps.findIndex(s => s.key === currentStep);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Progress bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {visibleSteps.map((step, index) => {
              const isActive = index === currentIndex;
              const isComplete = index < currentIndex;

              return (
                <div key={step.key} className="flex items-center flex-1">
                  {/* Step indicator */}
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isComplete
                          ? 'bg-blue-600 text-white'
                          : isActive
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isComplete ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium ${
                        isActive ? 'text-gray-900' : isComplete ? 'text-gray-600' : 'text-gray-400'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>

                  {/* Connector line */}
                  {index < visibleSteps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-4 ${
                        index < currentIndex ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 flex items-start justify-center p-4 py-8">
        <div className={`w-full ${wide ? 'max-w-5xl' : 'max-w-2xl'}`}>
          {children}
        </div>
      </div>
    </div>
  );
}
